import type { MortgagePart } from "@/models/Leningdeel";
import { useNumberParser } from "@/composables/useNumberParser";
import { repaymentFormLabels } from "@/components/existing-mortgage/repayment-form";

/**
 * Formats a value
 *
 * @param value - The value to format
 * @param minimumFractionDigits - Minimum count of numbers after comma
 * @param maximumFractionDigits - Maximum count of number after comma
 * @param useGrouping - Group the formatted number
 * @returns The formatted value OR undefined when the value is unformattable
 */
export function formatNumeric(
	value: unknown,
	minimumFractionDigits?: number,
	maximumFractionDigits?: number,
	useGrouping = true,
): string | undefined {
	let numVal: number | undefined;

	if (typeof value === "number") {
		numVal = value;
	} else if (typeof value === "string") {
		const parser = useNumberParser();
		numVal = parser.parse(value);
	}

	if (!Number.isFinite(numVal)) {
		return undefined;
	}

	const out = numVal?.toLocaleString("nl-NL", {
		minimumFractionDigits: minimumFractionDigits,
		maximumFractionDigits: maximumFractionDigits,
		useGrouping: useGrouping,
	});

	return out;
}

/**
 * Parse and format an input as a whole number
 *
 * @param value - The value to format and parse
 * @param useGrouping - If groups should be used in the parsed number.
 * @returns The parsed and formatted, whole numbered value OR undefined when value can't be parsed
 */
export function formatNumericWhole(
	value?: number | string,
	useGrouping = true,
): string | undefined {
	return formatNumeric(value, 0, 0, useGrouping);
}

/**
 * Format input to 3 decimal places
 *
 * @param value - The value to format
 * @returns The formatted value
 */
export function formatNumericPercentage(value: unknown) {
	return formatNumeric(value, 3, 3);
}

export function stringIsValue(
	value: unknown,
	replaceSign = "",
	valueWhenEmpty = 0.0,
): number | undefined {
	const parser = useNumberParser();

	let newValue: number | undefined;
	if (typeof value === "string") {
		const cleanedValue = value.replaceAll(" ", "").replaceAll(replaceSign, "");

		if (cleanedValue === "") {
			newValue = valueWhenEmpty;
		} else {
			newValue = parser.parse(cleanedValue);
		}
	} else if (typeof value === "number") {
		newValue = value;
	}

	if (!Number.isFinite(newValue)) {
		newValue = undefined;
	}

	return newValue;
}

export function asCur(value: unknown): number | undefined {
	return stringIsValue(value, "€");
}

export function asDisplayCur(value?: number | string): string {
	if (typeof value === "string") {
		value = asCur(value);
	}

	let display = "€ -";
	if (Number.isFinite(value)) {
		const formatted = formatNumericWhole(value as number);
		if (formatted !== undefined) display = `€ ${formatted}`;
	}

	return display;
}

export function isPercentage(value: unknown): number | undefined {
	return stringIsValue(value, "%");
}

function getDisplayDuration(timeInMonths?: number) {
	if (timeInMonths === undefined || !Number.isFinite(timeInMonths)) {
		return "";
	}

	const years = Math.floor(timeInMonths / 12);
	const months = timeInMonths % 12;

	return `${years} jaar${months > 0 ? ` en ${months} maanden` : ""}`;
}

function getDurationLabel(value: string | number | undefined, label: string) {
	const durationValue = asCur(value);
	const durationLabel = getDisplayDuration(durationValue);
	const durationIsNumber = Number.isFinite(durationValue) && value !== "";

	return durationIsNumber ? `${durationLabel} ${label}` : "";
}

export function getInterestLabel(value?: unknown) {
	const interest = stringIsValue(value, "", Number.NaN);

	return interest !== undefined && Number.isFinite(interest)
		? `${formatNumericPercentage(interest * 100)}%`
		: "";
}

/**
 * Generates the loanpart label for the PDF, containing all relevant loanpart information.
 *
 * @param loanpart - The loanpart for which to generate the label
 * @returns The generated label
 */
export function getLabelForPDF(loanpart: MortgagePart, idx?: number) {
	const rentevastLabel = getDurationLabel(
		loanpart.fixedInterestMonths,
		"rentevast",
	);
	const looptijdLabel = getDurationLabel(loanpart.durationMonths, "looptijd");
	const interestLabel = getInterestLabel(loanpart.interestRate);

	const boxLabel = loanpart.interestDeductionLaw === "none" ? "Box 3" : "Box 1";

	const outParts = [
		loanpart.repaymentForm
			? repaymentFormLabels[loanpart.repaymentForm]
			: `Leningdeel${idx !== undefined ? ` ${idx + 1}` : ""}`,
	];

	outParts.push(
		loanpart.existingMortgagePartId !== undefined ? "meenemen" : "nieuw",
	);

	if (rentevastLabel) {
		outParts.push(rentevastLabel);
	}

	if (looptijdLabel) {
		outParts.push(looptijdLabel);
	}

	if (interestLabel) {
		outParts.push(interestLabel);
	}

	if (boxLabel) {
		outParts.push(boxLabel);
	}

	return outParts.join(" - ");
}

/**
 * Checks if the element is editable
 *
 * @param element - The element to check
 * @returns True if the element is editable, false otherwise
 */
export function elementEditable(element: Element | null) {
	if (!element) {
		return false;
	}

	if (element.shadowRoot) {
		return elementEditable(element.shadowRoot.activeElement);
	}

	if (element instanceof HTMLElement && element.contentEditable === "true") {
		return true;
	}

	if (element.tagName === "INPUT" || element.tagName === "TEXTAREA") {
		return true;
	}
}
